import {
  Box,
  Flex,
  IconButton,
  Button,
  Text,
  Input,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";

import { FiEdit, FiTrash2, FiSearch, FiX } from "react-icons/fi";

import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";

import FormModal from "./FormModal";
import { ProcedimentoService } from "../../services/procedimento.service";

import ContentCard from "../../components/common/ContentCard";
import { PageHeader } from "../../components/common/PageHeader";

export default function ListPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [rows, setRows] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    procedimento: "",
  });

  const [openFilter, setOpenFilter] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      const data = await ProcedimentoService.findAll();
      const normalizedRows = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.data)
          ? (data as any).data
          : [];

      setRows(normalizedRows);
    } catch {
      toast({ title: "Erro ao carregar procedimentos", status: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDelete(id: number) {
    if (!confirm("Deseja excluir este procedimento?")) return;

    try {
      await ProcedimentoService.delete(id);
      toast({ title: "Procedimento excluído com sucesso", status: "success" });
      load();
    } catch {
      toast({ title: "Erro ao excluir procedimento", status: "error" });
    }
  }

  function renderFilter(field: string, label: string) {
    return openFilter === field ? (
      <Input
        size="sm"
        autoFocus
        placeholder={`Filtrar ${label}`}
        value={(filters as any)[field]}
        onChange={(e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }))}
        onBlur={() => setOpenFilter(null)}
      />
    ) : (
      <Flex align="center" gap={2}>
        <Text fontSize="sm" fontWeight="600">{label}</Text>
        <IconButton
          aria-label="filtro"
          icon={<FiSearch />}
          size="xs"
          variant="ghost"
          onClick={() => setOpenFilter(field)}
        />
      </Flex>
    );
  }

  const filtered = useMemo(() => {
    return rows.filter((item) => {
      const check = (field: any, filter: string) =>
        String(field ?? "").toLowerCase().includes(filter.toLowerCase());

      return check(item.procedimento, filters.procedimento);
    });
  }, [rows, filters]);

  const columns = [
    { name: renderFilter("procedimento", "Procedimento"), selector: (r: any) => r.procedimento, minWidth: "280px", wrap: true },
    {
      name: "Ações",
      width: "140px",
      cell: (row: any) => (
        <Flex gap={2}>
          <IconButton icon={<FiEdit />} size="sm" colorScheme="blue" onClick={() => { setEditing(row); onOpen(); }} />
          <IconButton icon={<FiTrash2 />} size="sm" colorScheme="red" onClick={() => handleDelete(row.id)} />
        </Flex>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Procedimentos"
        onNew={() => {
          setEditing(null);
          onOpen();
        }}
        actions={
          <Button
            leftIcon={<FiX />}
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={() => setFilters({ procedimento: "" })}
          >
            Limpar filtros
          </Button>
        }
      />

      <ContentCard>
        <DataTable
          columns={columns}
          data={filtered}
          pagination
          progressPending={loading}
          highlightOnHover
          responsive
          noDataComponent="Nenhum procedimento encontrado"
        />
      </ContentCard>

      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        initialData={editing}
        onSave={async (data: any) => {
          try {
            if (editing) {
              await ProcedimentoService.update(editing.id, data);
              toast({ title: "Procedimento atualizado com sucesso", status: "success" });
            } else {
              await ProcedimentoService.create(data);
              toast({ title: "Procedimento criado com sucesso", status: "success" });
            }
            load();
          } catch {
            toast({ title: "Erro ao salvar procedimento", status: "error" });
          } finally {
            onClose();
            setEditing(null);
          }
        }}
      />
    </Box>
  );
}
